/**
 * Octane AI Quiz Product Display Handler
 * Optimized for performance and maintainability
 * 
 * Features:
 * - Auto-selects recommended scent variant based on quiz answers
 * - Auto-selects one-time purchase option
 * - Displays product reviews and subtitles
 * - Dynamic price updates based on variant selection
 */

(function() {
    'use strict';

    // Configuration
    const CONFIG = {
        shopDomain: 'theearthlingco.com',
        cartAddUrl: 'https://theearthlingco.com/cart/add',
        debug: true // Set to false in production
    };

    // Utility functions
    const utils = {
        log: function(...args) {
            if (CONFIG.debug) {
                console.log(...args);
            }
        },
        
        logSection: function(title, data) {
            if (CONFIG.debug) {
                console.log('========== ' + title + ' ==========');
                console.log(data);
                if (typeof data === 'object') {
                    console.log('JSON:', JSON.stringify(data, null, 2));
                }
            }
        },

        extractVariantFromUrl: function(url) {
            if (!url || !url.includes('?variant')) return null;
            try {
                const urlParams = new URLSearchParams(url.split('?')[1]);
                return urlParams.get('variant');
            } catch (e) {
                const match = url.match(/[?&]variant=([^&]+)/);
                return match ? match[1] : null;
            }
        },

        extractQuizAnswer: function(answers, key) {
            if (!answers || typeof answers !== 'object') return null;
            
            // Direct key match
            if (answers[key] !== undefined) return answers[key];
            if (answers[key.toLowerCase()] !== undefined) return answers[key.toLowerCase()];
            
            // Case-insensitive search
            for (var prop in answers) {
                if (prop.toLowerCase() === key.toLowerCase()) {
                    return answers[prop];
                }
            }
            
            // Partial match search
            const lowerKey = key.toLowerCase();
            for (var prop in answers) {
                if (prop.toLowerCase().includes(lowerKey) || lowerKey.includes(prop.toLowerCase())) {
                    return answers[prop];
                }
            }
            
            return null;
        },

        findVariantOption: function(options, searchValue, searchType) {
            if (!options || options.length === 0) return null;
            
            const searchStr = String(searchValue).toLowerCase().trim();
            const optionsArray = Array.from(options);
            
            for (var i = 0; i < optionsArray.length; i++) {
                var opt = optionsArray[i];
                
                if (searchType === 'id') {
                    if (String(opt.value) === String(searchValue)) {
                        return opt;
                    }
                } else if (searchType === 'name') {
                    const optionText = opt.text.toLowerCase().trim();
                    // More flexible matching - check if search term is contained in option text
                    // or if option text is contained in search term
                    if (optionText === searchStr) {
                        return opt; // Exact match
                    }
                    // Check if search term words are all present in option text
                    var searchWords = searchStr.split(/\s+/);
                    var allWordsMatch = searchWords.every(function(word) {
                        return optionText.includes(word);
                    });
                    if (allWordsMatch && searchWords.length > 0) {
                        return opt;
                    }
                    // Also check reverse (option text words in search term)
                    var optionWords = optionText.split(/\s+/);
                    var allOptionWordsMatch = optionWords.every(function(word) {
                        return searchStr.includes(word);
                    });
                    if (allOptionWordsMatch && optionWords.length > 0) {
                        return opt;
                    }
                }
            }
            
            return null;
        },

        formatPrice: function(cents) {
            return (cents / 100).toFixed(2);
        },

        formatReviewCount: function(count) {
            if (!count) return '0';
            if (count >= 1000) {
                const thousands = Math.floor(count / 1000);
                const remainder = count % 1000;
                return thousands + ',' + String(remainder).padStart(3, '0');
            }
            return String(count);
        },

        getRatingPercentage: function(rating) {
            if (rating >= 4.5) return 100;
            if (rating >= 4.0) return 90;
            return 80;
        }
    };

    /**
     * Collapse a title Octane has doubled, e.g.
     *   "Hair Health Gummies Hair Health Gummies" -> "Hair Health Gummies"
     *
     * Octane composes the title of a *variant* entry as "<variant copy> <product copy>".
     * That reads correctly when the variant has copy of its own ("Rosy Jasmine Shampoo
     * and Conditioner for Moisture & Strength"), but when it has none Octane falls back
     * to the product name for both halves and you get the name twice. It only shows up
     * here because we render octaneProduct.title verbatim; Octane's own result renderer
     * happens to print it once.
     *
     * Only an exact "X X" is collapsed, so genuine titles are left alone.
     */
    function collapseRepeatedTitle(title) {
        if (!title) return title;
        var normalized = String(title).trim().replace(/\s+/g, ' ');
        var repeated = normalized.match(/^(.+) \1$/);
        return repeated ? repeated[1] : normalized;
    }

    // Scent quiz only (flag set in theme.liquid): Octane's Product Info custom title and
    // description, as { productId: { title: field, description: field } } where a field is
    // { all: text, variants: { variantId: { copy: text } } }. "all" is product-level copy
    // (Body Wash Bar Sampler), "variants" is per scent (S&C). Filled per quiz result.
    var octaneVariantCopy = {};

    function readOctaneCopyField(field) {
        if (!field) return null;
        var perVariant = field.apply_to === 'per_variant';
        return {
            all: perVariant ? '' : (field.copy || ''),
            variants: perVariant ? (field.variants || {}) : {}
        };
    }

    function octaneCopyFor(field, variantId) {
        if (!field) return '';
        var entry = field.variants[String(variantId)];
        return (entry && entry.copy) || field.all;
    }

    function storeOctaneVariantCopy(detail) {
        octaneVariantCopy = {};
        if (!window.quizShowOctaneVariantCopy) return;
        var data = detail && detail.state && detail.state.info && detail.state.info.customized_products_data;
        if (!data) return;
        Object.keys(data).forEach(function(productId) {
            if (!data[productId]) return;
            octaneVariantCopy[productId] = {
                title: readOctaneCopyField(data[productId].title),
                description: readOctaneCopyField(data[productId].product_description)
            };
        });
    }

    // Apply the selected variant's Octane custom title (falls back to the card's original
    // title) and show its description under the card subtitle. No-op without Octane copy.
    function updateVariantCopy(template, productId, variantId) {
        var productCopy = octaneVariantCopy[productId];
        if (!productCopy) return;

        var titleEl = template.querySelector('.title');
        if (titleEl) {
            if (!titleEl.hasAttribute('data-original-title')) {
                titleEl.setAttribute('data-original-title', titleEl.innerText);
            }
            var title = octaneCopyFor(productCopy.title, variantId) || titleEl.getAttribute('data-original-title');
            titleEl.innerText = title;
            var imgEl = template.querySelector('img');
            if (imgEl) imgEl.alt = title;
        }

        var copyEl = template.querySelector('.octane-variant-copy');
        if (!copyEl) {
            var subtitleEl = template.querySelector('.product-subtitle');
            if (!subtitleEl) return;
            copyEl = document.createElement('div');
            copyEl.className = 'octane-variant-copy';
            copyEl.style.cssText = 'font-size: 15px; line-height: 1.5; color: #3B332F; text-align: left;';
            subtitleEl.parentNode.insertBefore(copyEl, subtitleEl.nextSibling);
        }
        var html = octaneCopyFor(productCopy.description, variantId);
        copyEl.innerHTML = html;
        copyEl.style.display = html ? 'block' : 'none';
    }

    // Extract recommended variant/scent from Octane AI product object
    function extractRecommendedVariant(product) {
        var recommendedVariantId = product.variant_id || product.variantId || null;
        var recommendedScent = product.scent || product.variant_title || product.variant || null;

        // Scent quiz only (flag set in theme.liquid): Octane ranks the variants best
        // match first and sends no variant_id, so take the top-ranked variant.
        if (!recommendedVariantId && window.quizUseOctaneRankedVariant &&
            Array.isArray(product.variants) && product.variants.length > 1 &&
            product.variants[0] && product.variants[0].id) {
            recommendedVariantId = String(product.variants[0].id);
        }
        
        // Extract variant from product title (e.g., "Citrus Sun Shampoo..." -> "Citrus Sun")
        if (product.title && !recommendedScent) {
            // Common patterns: "Citrus Sun Shampoo...", "Simple Fresh Shampoo..."
            var titleParts = product.title.split(' ');
            if (titleParts.length >= 2) {
                // Check if first two words match a variant name
                var possibleScent = titleParts[0] + ' ' + titleParts[1];
                recommendedScent = possibleScent;
            } else if (titleParts.length === 1) {
                recommendedScent = titleParts[0];
            }
        }
        
        // Check product URL for variant
        if (product.link) {
            const urlVariantId = utils.extractVariantFromUrl(product.link);
            if (urlVariantId && !recommendedVariantId) {
                recommendedVariantId = urlVariantId;
            }
        }
        
        // Check custom properties
        if (product.properties && typeof product.properties === 'object') {
            for (var prop in product.properties) {
                if (prop.toLowerCase().includes('scent') || prop.toLowerCase().includes('variant')) {
                    recommendedScent = product.properties[prop];
                    break;
                }
            }
        }
        
        return {
            variantId: recommendedVariantId,
            scent: recommendedScent
        };
    }

    // Extract variant recommendation from section 0's bottom_content
    function extractVariantFromSection0(sections) {
        if (!sections || !Array.isArray(sections) || sections.length === 0) return null;
        
        var section0 = sections[0];
        if (!section0 || !section0.bottom_content || !Array.isArray(section0.bottom_content) || section0.bottom_content.length === 0) {
            return null;
        }
        
        var bottomContent0 = section0.bottom_content[0];
        if (!bottomContent0 || !bottomContent0.text) {
            return null;
        }
        
        var text = bottomContent0.text;
        
        // List of possible variant names to search for
        var variantNames = [
            'Vanilla Coconut',
            'Sweet Sandalwood',
            'Citrus Sun',
            'Wild Lavender',
            'Simple Fresh',
            'Cool Breeze',
            'Unscented'
        ];
        
        // Search for variant names in the text (case-insensitive)
        var lowerText = text.toLowerCase();
        for (var i = 0; i < variantNames.length; i++) {
            var variantName = variantNames[i];
            if (lowerText.indexOf(variantName.toLowerCase()) !== -1) {
                return variantName;
            }
        }
        
        return null;
    }

    // Auto-select variant based on priority
    function autoSelectVariant(selectElement, recommended, vacationAnswer, variantFromUrl, firstAvailableVariantId, template, section0Variant) {
        if (!selectElement || selectElement.options.length === 0) return null;
        
        var selectedOption = null;
        var selectedVariantId = null;
        var priorityUsed = null;
        
        // Priority 1: Variant from section 0's bottom_content (highest priority)
        if (section0Variant) {
            selectedOption = utils.findVariantOption(selectElement.options, section0Variant, 'name');
            if (selectedOption) {
                selectedVariantId = selectedOption.value;
                priorityUsed = 1;
                return { option: selectedOption, variantId: selectedVariantId, priorityUsed: priorityUsed };
            }
        }
        
        // Priority 2: Recommended variant ID from Octane AI
        if (recommended.variantId) {
            selectedOption = utils.findVariantOption(selectElement.options, recommended.variantId, 'id');
            if (selectedOption) {
                selectedVariantId = recommended.variantId;
                priorityUsed = 2;
                return { option: selectedOption, variantId: selectedVariantId, priorityUsed: priorityUsed };
            }
        }
        
        // Priority 3: Recommended scent name match (from title or Octane AI)
        if (recommended.scent) {
            selectedOption = utils.findVariantOption(selectElement.options, recommended.scent, 'name');
            if (selectedOption) {
                selectedVariantId = selectedOption.value;
                priorityUsed = 3;
                return { option: selectedOption, variantId: selectedVariantId, priorityUsed: priorityUsed };
            }
        }
        
        // Priority 4: Vacation answer match
        if (vacationAnswer) {
            selectedOption = utils.findVariantOption(selectElement.options, vacationAnswer, 'name');
            if (selectedOption) {
                selectedVariantId = selectedOption.value;
                priorityUsed = 4;
                return { option: selectedOption, variantId: selectedVariantId, priorityUsed: priorityUsed };
            }
        }
        
        // Priority 5: Variant from URL parameter
        if (variantFromUrl) {
            selectedOption = utils.findVariantOption(selectElement.options, variantFromUrl, 'id');
            if (selectedOption) {
                selectedVariantId = variantFromUrl;
                priorityUsed = 5;
                return { option: selectedOption, variantId: selectedVariantId, priorityUsed: priorityUsed };
            }
        }
        
        // Priority 6: First available variant (fallback)
        if (selectElement.options.length > 0) {
            selectedOption = selectElement.options[0];
            selectedVariantId = selectedOption.value;
            priorityUsed = 6;
            return { option: selectedOption, variantId: selectedVariantId, priorityUsed: priorityUsed };
        }
        
        return null;
    }

    // Get product metafields from Liquid-rendered hidden elements
    function getProductMetafields(productHandle) {
        var metafieldEl = document.querySelector('.quiz-product-metafield.' + productHandle);
        if (!metafieldEl) {
            return null;
        }
        
        var ratingEl = metafieldEl.querySelector('.rating-average');
        var countEl = metafieldEl.querySelector('.rating-count');
        var subtitleEl = metafieldEl.querySelector('.cp-subtitle');
        
        var metafields = {
            rating: null,
            ratingCount: null,
            ratingPercentage: null,
            subtitle: null
        };
        
        if (ratingEl) {
            metafields.rating = parseFloat(ratingEl.getAttribute('data-rating'));
            metafields.ratingPercentage = parseInt(ratingEl.getAttribute('data-rating-percentage'));
        }
        
        if (countEl) {
            metafields.ratingCount = parseInt(countEl.getAttribute('data-count'));
        }
        
        if (subtitleEl) {
            metafields.subtitle = subtitleEl.getAttribute('data-subtitle');
        }
        
        return metafields;
    }

    // Update product display with reviews - get from Liquid-rendered metafields
    function updateProductReviews(template, productHandle, productUrl) {
        var ratingStarsEl = template.querySelector(".loop-rating-row .native_rating_stars");
        var ratingTextEl = template.querySelector(".loop-rating-row .rating-text");
        var ratingRow = ratingStarsEl ? ratingStarsEl.parentElement : null;
        
        if (!ratingStarsEl) {
            if (ratingRow) ratingRow.style.display = "none";
            return;
        }
        
        // Get metafields from Liquid-rendered hidden elements
        var metafields = getProductMetafields(productHandle);
        
        if (!metafields) {
            if (ratingRow) ratingRow.style.display = "none";
            return;
        }
        
        var rating = metafields.rating;
        var ratingCount = metafields.ratingCount;
        var ratingPercentage = metafields.ratingPercentage;
        
        if (rating && ratingPercentage !== null) {
            ratingStarsEl.className = "native_rating_stars stars_num_" + ratingPercentage;
            ratingStarsEl.setAttribute("aria-label", "Rating of this product is " + rating + " out of 5.");
            
            if (ratingTextEl) {
                // Just show the rating number, no "/5" or review count
                ratingTextEl.innerText = rating.toString();
            }
            if (ratingRow) {
                ratingRow.style.display = "flex";
            }
        } else {
            if (ratingRow) ratingRow.style.display = "none";
        }
    }

    // Update product subtitle - get from Liquid-rendered metafields
    function updateProductSubtitle(template, productHandle, productUrl) {
        var subtitleEl = template.querySelector(".product-subtitle");
        if (!subtitleEl) {
            return;
        }
        
        // Get metafields from Liquid-rendered hidden elements
        var metafields = getProductMetafields(productHandle);
        
        if (!metafields) {
            subtitleEl.style.display = "none";
            return;
        }
        
        var subtitleText = metafields.subtitle;
        
        if (subtitleText && subtitleText.trim() !== '') {
            // Clean up "Read More >" if present and just show the subtitle text
            var cleanSubtitle = String(subtitleText).replace(/Read More\s*>?/gi, '').trim();
            subtitleEl.innerText = cleanSubtitle;
            subtitleEl.style.display = "block";
        } else {
            subtitleEl.style.display = "none";
        }
    }

    // Update product price display
    function updateProductPrice(template, variant, fallbackPrice) {
        var priceElement = template.querySelector(".price--end .money");
        var priceWithoutDiscount = template.querySelector(".price--without-discount");
        var priceWithoutDiscountMoney = priceWithoutDiscount ? priceWithoutDiscount.querySelector(".money") : null;
        
        if (!priceElement) return;
        
        if (variant) {
            var variantPrice = utils.formatPrice(variant.price);
            priceElement.innerText = "$" + variantPrice;
            
            // Update aria-label based on whether there's a discount
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                // Discounted price
                priceElement.setAttribute("aria-label", "Discounted price");
                
                // Show and set original price
                if (priceWithoutDiscount && priceWithoutDiscountMoney) {
                    var comparePrice = utils.formatPrice(variant.compare_at_price);
                    priceWithoutDiscountMoney.innerText = "$" + comparePrice;
                    priceWithoutDiscount.style.display = "inline";
                }
            } else {
                // Regular price
                priceElement.setAttribute("aria-label", "Price");
                
                // Hide original price
                if (priceWithoutDiscount) {
                    priceWithoutDiscount.style.display = "none";
                }
            }
        } else if (fallbackPrice) {
            priceElement.innerText = fallbackPrice;
            // Default to "Price" aria-label when using fallback
            priceElement.setAttribute("aria-label", "Price");
        }
    }

    // Process product variants and populate selectors
    function processProductVariants(product, selectVariant, indexAvailableArray) {
        var lowercasedImagesArray = product.images ? product.images.map(function(img) { return img.toLowerCase(); }) : [];
        var croppedImage = product.featured_image || (product.images && product.images[0]) || '';
        
        for (var i = 0; i < product.variants.length; i++) {
            if (!product.variants[i].available) continue;
            
            indexAvailableArray.push(i);
            
            var variantId = product.variants[i].id;
            var variantTitle = product.variants[i].title;
            
            if (product.variants.length > 1) {
                var opt = new Option(variantTitle, variantId);
                opt.setAttribute('data-variant-price', utils.formatPrice(product.variants[i].price));
                
                // Store compare_at_price if available
                if (product.variants[i].compare_at_price && product.variants[i].compare_at_price > product.variants[i].price) {
                    opt.setAttribute('data-variant-compare-price', utils.formatPrice(product.variants[i].compare_at_price));
                }
                
                // Set variant image
                var variantFeaturedImageSrc = product.variants[i].featured_image && product.variants[i].featured_image.src;
                if (variantFeaturedImageSrc) {
                    opt.setAttribute("data-varimg", variantFeaturedImageSrc);
                } else if (lowercasedImagesArray.length > 0) {
                    var handleizeImageTitle = variantTitle.includes('Poplar') 
                        ? variantTitle.replaceAll(' ', '').replaceAll('"', '').replaceAll('(', '').replaceAll(')', '').toLowerCase()
                        : variantTitle.replaceAll(' ', '-').replaceAll('"', '').replaceAll('(', '').replaceAll(')', '').replaceAll('-/-', '-').toLowerCase();
                    
                    var varImgIndex = lowercasedImagesArray.findIndex(function(element) {
                        return element.includes(handleizeImageTitle);
                    });
                    
                    if (varImgIndex !== -1) {
                        opt.setAttribute("data-varimg", product.images[varImgIndex]);
                        if (i === indexAvailableArray[0]) {
                            croppedImage = product.images[varImgIndex];
                        }
                    }
                }
                
                selectVariant.options[selectVariant.options.length] = opt;
                selectVariant.classList.remove('hidden');
            }
        }
        
        return croppedImage;
    }

    // Setup subscription selector
    function setupSubscriptionSelector(product, selectSubscr) {
        if (!product.selling_plan_groups || product.selling_plan_groups.length === 0) return;
        
        var sPlans = product.selling_plan_groups;
        var subGroup = sPlans.find(function(g) {
            var nameMatch = g.name && g.name.toLowerCase().includes('subscribe');
            var hasFrequency = Array.isArray(g.options) && g.options.some(function(o) { return o && o.name === 'Frequency'; });
            return nameMatch || hasFrequency;
        }) || sPlans[0];
        
        if (subGroup && subGroup.selling_plans && subGroup.selling_plans.length > 0) {
            selectSubscr.classList.remove("hidden");
            
            var oneTimeOption = document.createElement("option");
            oneTimeOption.value = "1";
            oneTimeOption.text = "One-time";
            selectSubscr.appendChild(oneTimeOption);
            
            subGroup.selling_plans.forEach(function(plan) {
                var spOpt = document.createElement("option");
                spOpt.value = plan.id;
                spOpt.text = plan.name;
                selectSubscr.appendChild(spOpt);
            });
            
            // Auto-select "One-time" purchase
            selectSubscr.value = "1";
        }
    }

    // Process a single product
    function processProduct(octaneProduct, index, template, vacationAnswer, productsContainer, section0Variant, scalpFeelAnswer) {
        var productUrl = octaneProduct.link || '';
        var variantFromUrl = utils.extractVariantFromUrl(productUrl);
        var cleanProductUrl = productUrl.split('?')[0];
        
        // Extract recommended variant info
        var recommended = extractRecommendedVariant(octaneProduct);
        
        // Extract image from Octane AI product object
        var octaneImage = octaneProduct.image || null;
        
        var productHandle = cleanProductUrl.replace('https://' + CONFIG.shopDomain + '/products/', '');
        var fullUrl = cleanProductUrl + '.js';
        
        var selectVariant = template.querySelector('.upsells_variants');
        var selectSubscr = template.querySelector('.subscriptions');
        
            // Fetch product data from Shopify
        jQuery.getJSON(fullUrl, function(shopifyProduct) {
            // reserved-for-subs: stock is held for Loop renewals, never recommend it for purchase
            if (!shopifyProduct.available || (shopifyProduct.tags || []).indexOf('reserved-for-subs') > -1) {
                template.classList.add('hidden');
                return;
            }
            
            // Check if this is a product that should prioritize variant images
            var productId = String(shopifyProduct.id);
            var prioritizeVariantImage = productId === '7162093600947';
            
            var indexAvailableArray = [];
            var croppedImage = processProductVariants(shopifyProduct, selectVariant, indexAvailableArray);
            
            // Use Octane image as initial image if available (unless this product prioritizes variant images)
            if (octaneImage && !prioritizeVariantImage) {
                croppedImage = octaneImage;
            }
            
            if (indexAvailableArray.length === 0) {
                template.classList.add('hidden');
                return;
            }
            
            // Setup subscription selector
            setupSubscriptionSelector(shopifyProduct, selectSubscr);
            
            // Get first available variant (fallback)
            var firstAvailableVariantId = shopifyProduct.variants[indexAvailableArray[0]].id;
            
            // Check if recommended variant exists in product but is out of stock
            var recommendedVariantOutOfStock = false;
            
            if (recommended.variantId) {
                var recommendedVariantObj = shopifyProduct.variants.find(function(v) {
                    return String(v.id) === String(recommended.variantId);
                });
                
                if (recommendedVariantObj) {
                    // Check if this variant is NOT in the available variants list
                    var isAvailable = indexAvailableArray.some(function(idx) {
                        return String(shopifyProduct.variants[idx].id) === String(recommended.variantId);
                    });
                    if (!isAvailable) {
                        recommendedVariantOutOfStock = true;
                    }
                }
            } else if (recommended.scent) {
                // Check if recommended scent matches an unavailable variant
                var recommendedScentVariant = shopifyProduct.variants.find(function(v) {
                    return v.title && v.title.toLowerCase().indexOf(recommended.scent.toLowerCase()) !== -1;
                });
                
                if (recommendedScentVariant) {
                    var isAvailable = indexAvailableArray.some(function(idx) {
                        return String(shopifyProduct.variants[idx].id) === String(recommendedScentVariant.id);
                    });
                    if (!isAvailable) {
                        recommendedVariantOutOfStock = true;
                    }
                }
            }
            
            // Auto-select variant (only if recommended variant is available, otherwise don't select anything)
            var selectedVariant = null;
            var finalVariantId = null;
            
            if (!recommendedVariantOutOfStock) {
                selectedVariant = autoSelectVariant(
                    selectVariant, 
                    recommended, 
                    vacationAnswer, 
                    variantFromUrl, 
                    firstAvailableVariantId,
                    template,
                    section0Variant
                );
                
                finalVariantId = selectedVariant ? selectedVariant.variantId : firstAvailableVariantId;
                
                // Apply variant selection
                if (selectedVariant && selectedVariant.option) {
                    selectVariant.value = selectedVariant.variantId;
                    var imgVarUrl = selectedVariant.option.getAttribute('data-varimg');
                    // For product ID 7162093600947, prioritize variant image; otherwise prioritize Octane image
                    if (prioritizeVariantImage && imgVarUrl) {
                        template.querySelector("img").src = imgVarUrl;
                        croppedImage = imgVarUrl;
                    } else if (octaneImage) {
                        template.querySelector("img").src = octaneImage;
                        croppedImage = octaneImage;
                    } else if (imgVarUrl) {
                        // Only use variant image if Octane image is not available
                        template.querySelector("img").src = imgVarUrl;
                        croppedImage = imgVarUrl;
                    }
                } else if (selectVariant.options.length > 0) {
                    selectVariant.value = selectVariant.options[0].value;
                }
            } else {
                // Recommended variant is out of stock - don't select anything, disable button
                finalVariantId = null;
                selectVariant.value = '';
            }

            var forcedVariantImage = false;
            // Force Unscented for Shampoo & Conditioner Bar when scalp feel is sensitive
            var normalizedScalpFeel = (scalpFeelAnswer || '').toString().toLowerCase().trim();
            var isSensitiveScalpFeel = normalizedScalpFeel.includes('prone to irritation');
            var isShampooAndConditionerBar = shopifyProduct.handle === 'shampoo-and-conditioner-bar' ||
                template.classList.contains('shampoo-and-conditioner-bar');
            if (isSensitiveScalpFeel && isShampooAndConditionerBar && selectVariant && selectVariant.options.length > 0) {
                var unscentedOption = utils.findVariantOption(selectVariant.options, 'Unscented', 'name');
                if (unscentedOption) {
                    selectVariant.value = unscentedOption.value;
                    finalVariantId = unscentedOption.value;
                    selectedVariant = { option: unscentedOption, variantId: unscentedOption.value, priorityUsed: 'scalp-feel' };
                    var unscentedImg = unscentedOption.getAttribute('data-varimg');
                    if (unscentedImg) {
                        croppedImage = unscentedImg;
                        forcedVariantImage = true;
                    }
                    selectVariant.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
            
            // Update product title - use Octane title or fallback to Shopify product title
            var productTitle = collapseRepeatedTitle(octaneProduct.title) || shopifyProduct.title || '';
            
            // Update product information - prioritize based on product ID
            var imgElement = template.querySelector("img");
            if (imgElement) {
                // For forced variant selection, keep the variant image
                if (forcedVariantImage && croppedImage) {
                    imgElement.src = croppedImage;
                // For product ID 7162093600947, variant image is already set; otherwise prioritize Octane image
                } else if (!prioritizeVariantImage && octaneImage) {
                    imgElement.src = octaneImage;
                } else {
                    imgElement.src = croppedImage;
                }
            }
            template.querySelector("img").alt = productTitle;
            template.querySelector(".title").innerText = productTitle;
            
            // Update product links
            var productLinks = template.querySelectorAll('.recommended-product-card a[itemprop="url"]');
            productLinks.forEach(function(link) {
                link.setAttribute("href", cleanProductUrl);
            });
            template.querySelector('.product-info__caption').setAttribute("href", cleanProductUrl);
            
            // Update price
            var selectedVariantData = null;
            if (finalVariantId) {
                selectedVariantData = shopifyProduct.variants.find(function(v) {
                    return String(v.id) === String(finalVariantId);
                });
            }
            updateProductPrice(template, selectedVariantData, octaneProduct.price_formatted || octaneProduct.ending_price_formatted);
            
            // Update add to cart button
            var addToCartBtn = template.querySelector(".upsell-addtocart");
            var addToCartText = addToCartBtn.querySelector("span");
            if (recommendedVariantOutOfStock || !finalVariantId) {
                // Show "Sold Out" and disable button
                if (addToCartText) {
                    addToCartText.innerText = "Sold Out";
                } else {
                    addToCartBtn.innerText = "Sold Out";
                }
                addToCartBtn.setAttribute("href", "#");
                addToCartBtn.setAttribute("data-variant", "");
                addToCartBtn.style.opacity = "0.5";
                addToCartBtn.style.cursor = "not-allowed";
                addToCartBtn.style.pointerEvents = "none";
            } else {
                // Normal add to cart functionality
                var linkVariant = CONFIG.cartAddUrl + "?id=" + finalVariantId + "&quantity=1";
                addToCartBtn.setAttribute("href", linkVariant);
                addToCartBtn.setAttribute("data-variant", finalVariantId);
                addToCartBtn.style.opacity = "";
                addToCartBtn.style.cursor = "";
                addToCartBtn.style.pointerEvents = "";
                // Reset button text if it was "Sold Out"
                if (addToCartText) {
                    if (addToCartText.innerText === "Sold Out") {
                        addToCartText.innerText = "Add to cart";
                    }
                } else if (addToCartBtn.innerText === "Sold Out") {
                    addToCartBtn.innerText = "Add to cart";
                }
            }
            
            template.classList.add(shopifyProduct.handle);
            
            // Append to products container
            if (productsContainer) {
                productsContainer.appendChild(template);
            }
            
            // Update reviews and subtitle AFTER template is in DOM
            // Get from Liquid-rendered metafields (no API call needed)
            updateProductReviews(template, productHandle, cleanProductUrl);
            updateProductSubtitle(template, productHandle, cleanProductUrl);
            template.setAttribute('data-product-id', shopifyProduct.id);
            if (finalVariantId) {
                updateVariantCopy(template, String(shopifyProduct.id), finalVariantId);
            }
        }).fail(function(jqXHR, textStatus, errorThrown) {
            template.classList.add('hidden');
        });
    }

    // Render content blocks (top_content or bottom_content)
    function renderContentBlocks(contentBlocks, container) {
        if (!contentBlocks || !Array.isArray(contentBlocks) || contentBlocks.length === 0) {
            return;
        }
        
        contentBlocks.forEach(function(contentBlock) {
            var contentEl = null;
            
            if (contentBlock.content_type === 'text') {
                if (contentBlock.text_type === 'title') {
                    contentEl = document.createElement('h2');
                    contentEl.className = 'octane-section-title';
                    contentEl.style.cssText = 'font-size: 24px; font-weight: 600; color: #3B332F; margin: 20px 0 16px; text-align: left; width: 100%;';
                } else if (contentBlock.text_type === 'description') {
                    contentEl = document.createElement('p');
                    contentEl.className = 'octane-section-description';
                    contentEl.style.cssText = 'font-size: 16px; color: #666; margin: 0 0 16px; text-align: left; width: 100%;';
                } else {
                    contentEl = document.createElement('div');
                    contentEl.className = 'octane-section-content';
                    contentEl.style.cssText = 'font-size: 16px; color: #3B332F; margin: 0 0 16px; text-align: left; width: 100%;';
                }
                
                if (contentBlock.text) {
                    contentEl.innerText = contentBlock.text;
                    container.appendChild(contentEl);
                }
            } else if (contentBlock.content_type === 'html') {
                contentEl = document.createElement('div');
                contentEl.className = 'octane-section-html';
                contentEl.style.cssText = 'margin: 0 0 16px; width: 100%;';
                if (contentBlock.html) {
                    contentEl.innerHTML = contentBlock.html;
                    container.appendChild(contentEl);
                }
            } else if (contentBlock.content_type === 'rich_editor') {
                // Handle rich_editor content type with rich_editor property
                contentEl = document.createElement('div');
                contentEl.className = 'octane-section-rich-editor';
                contentEl.style.cssText = 'margin: 0 0 16px; width: 100%;';
                if (contentBlock.rich_editor) {
                    contentEl.innerHTML = contentBlock.rich_editor;
                    container.appendChild(contentEl);
                }
            } else if (contentBlock.description) {
                // Handle product blocks that have description directly (fallback)
                contentEl = document.createElement('div');
                contentEl.className = 'octane-section-description';
                contentEl.style.cssText = 'margin: 0 0 16px; width: 100%;';
                contentEl.innerHTML = contentBlock.description;
                container.appendChild(contentEl);
            }
        });
    }

    // Process a single section and return the container (don't append it)
    function processSection(section, sectionIndex, vacationAnswer, productCardTemplate, globalProductIndex, section0Variant, skipBottomContent, scalpFeelAnswer) {
        globalProductIndex = globalProductIndex || { value: 0 }; // Use object to pass by reference
        
        // Create section container
        var sectionContainer = document.createElement('div');
        sectionContainer.className = 'octane-section';
        sectionContainer.setAttribute('data-section-id', section.id || sectionIndex);
        sectionContainer.style.cssText = 'width: 100%; margin-bottom: 40px;';
        
        // Render top content
        if (section.top_content && Array.isArray(section.top_content)) {
            renderContentBlocks(section.top_content, sectionContainer);
        }
        
        // Create products container for this section
        var sectionProductsContainer = document.createElement('div');
        sectionProductsContainer.className = 'octane-section-products';
        sectionProductsContainer.style.cssText = 'display: flex; flex-wrap: wrap; width: 100%;';
        
        // Create single container for all products
        var productsContainer = document.createElement('div');
        productsContainer.className = 'products-container';
        productsContainer.style.cssText = 'display: flex; flex-wrap: wrap; width: 100%; gap: 10px;';
        
        // Process products in this section
        if (section.products && Array.isArray(section.products)) {
            section.products.forEach(function(product, productIndex) {
                var template = productCardTemplate.cloneNode(true);
                var currentIndex = globalProductIndex.value;
                
                processProduct(product, currentIndex, template, vacationAnswer, productsContainer, section0Variant, scalpFeelAnswer);
                
                globalProductIndex.value++; // Increment global index
            });
        }
        
        // Append products container if it has content
        if (productsContainer.children.length > 0) {
            sectionProductsContainer.appendChild(productsContainer);
        }
        
        sectionContainer.appendChild(sectionProductsContainer);
        
        // Render bottom content (ignore for section 0, skip for section 4 if skipBottomContent is true)
        if (sectionIndex !== 0 && !skipBottomContent && section.bottom_content && Array.isArray(section.bottom_content) && section.bottom_content.length > 0) {
            renderContentBlocks(section.bottom_content, sectionContainer);
        }
        
        // Return both the container and updated index
        return {
            container: sectionContainer,
            globalProductIndex: globalProductIndex
        };
    }

    // Main handler function
    function handleQuizResults(e) {
        utils.logSection('OCTANE AI QUIZ EVENT OBJECT', e);
        utils.logSection('EVENT DETAIL', e.detail);
        
        storeOctaneVariantCopy(e.detail);

        // Extract quiz answers
        var quizAnswers = e.detail.answers || e.detail.quiz_data || {};
        var vacationAnswer = utils.extractQuizAnswer(quizAnswers, 'vacation');
        var scalpFeelAnswer = utils.extractQuizAnswer(quizAnswers, 'Octane: Scalp Feel');
        
        // Setup DOM elements
        var list = document.querySelector(".octane-ai-quiz-rendered");
        if (!list) {
            return;
        }
        
        var productCardTemplate = document.querySelector(".quiz-items-layout .recommended-product-card");
        if (!productCardTemplate) {
            return;
        }
        
        list.classList.add('display_flex');
        
        var listToAppendTo = list.querySelector('[data-element*="oct-quiz-resultitem"]');
        
        var quizContainer = document.querySelector(".quiz-wrapper");
        if (quizContainer) {
            quizContainer.classList.add('overlayed');
        }
        
        // Check for sections first (new format)
        var sections = e.detail.sections || e.detail.product_blocks;
        
        if (sections && Array.isArray(sections)) {
            // Extract variant recommendation from section 0's bottom_content (if available)
            var section0Variant = extractVariantFromSection0(sections);
            
            // Check if first item has sections structure (has top_content, bottom_content, products)
            var isSectionsFormat = sections.length > 0 && 
                (sections[0].top_content !== undefined || sections[0].bottom_content !== undefined || 
                 (sections[0].products && Array.isArray(sections[0].products)));
            
            if (isSectionsFormat) {
                // Set AJAX to synchronous to maintain product order
                jQuery.ajaxSetup({ async: false });
                
                // Track global product index across all sections
                var globalProductIndex = { value: 0 };
                
                // Collect all section containers to append in order
                var sectionContainers = [];
                
                // Process each section and collect containers
                sections.forEach(function(section, sectionIndex) {
                    // Skip bottom_content for section 4 (we'll render it separately as button)
                    var skipBottomContent = (sectionIndex === 4);
                    var result = processSection(section, sectionIndex, vacationAnswer, productCardTemplate, globalProductIndex, section0Variant, skipBottomContent, scalpFeelAnswer);
                    sectionContainers.push(result.container);
                    globalProductIndex = result.globalProductIndex;
                });
                
                // Create wrapper for sections 1-3 to display side by side
                var sections123Wrapper = document.createElement('div');
                sections123Wrapper.className = 'octane-sections-123-wrapper';
                sections123Wrapper.style.cssText = 'width: 100%;';
                
                var targetParent = listToAppendTo && listToAppendTo.parentElement 
                    ? listToAppendTo.parentElement 
                    : document.querySelector(".octane-ai-quiz-rendered");
                
                if (!targetParent) {
                    jQuery.ajaxSetup({ async: true });
                    return;
                }
                
                // Extract section 4's bottom_content for the button (if section 4 exists)
                var section4ButtonContainer = null;
                if (sections.length > 4) {
                    var section4 = sections[4];
                    if (section4 && section4.bottom_content && Array.isArray(section4.bottom_content) && section4.bottom_content.length > 0) {
                        section4ButtonContainer = document.createElement('div');
                        section4ButtonContainer.className = 'octane-section-4-button';
                        section4ButtonContainer.style.cssText = 'width: 100%; margin: 40px 0;';
                        renderContentBlocks(section4.bottom_content, section4ButtonContainer);
                    }
                }
                
                // Append all sections in order (0, 1, 2 in wrapper, 3+ separately)
                sectionContainers.forEach(function(sectionContainer, index) {
                    if (index >= 0 && index <= 2) {
                        // Sections 0-2 (Steps 1-3) - add to wrapper for side-by-side display
                        sections123Wrapper.appendChild(sectionContainer);
                        // After section 2 (index 2), append the wrapper
                        if (index === 2) {
                            targetParent.appendChild(sections123Wrapper);
                            // Append section 4 button immediately after wrapper
                            if (section4ButtonContainer) {
                                targetParent.appendChild(section4ButtonContainer);
                            }
                        }
                    } else if (index === 3) {
                        // Section 3 - only append if it has products (might be empty)
                        var hasProducts = sectionContainer.querySelector('.products-container') &&
                                        sectionContainer.querySelector('.products-container').children.length > 0;
                        if (hasProducts) {
                            targetParent.appendChild(sectionContainer);
                        }
                    } else if (index === 4) {
                        // Section 4 - only append if it has products (bottom_content is already rendered as button)
                        var hasProducts = sectionContainer.querySelector('.products-container') &&
                                        sectionContainer.querySelector('.products-container').children.length > 0;
                        if (hasProducts) {
                            targetParent.appendChild(sectionContainer);
                        }
                    } else {
                        // Section 5+ - append directly
                        targetParent.appendChild(sectionContainer);
                    }
                });
                
                // Safety check: Append wrapper if it has children and hasn't been appended yet
                // (This handles edge cases where section 2 might not exist)
                if (sections123Wrapper.children.length > 0 && !sections123Wrapper.parentElement) {
                    targetParent.appendChild(sections123Wrapper);
                    // Append section 4 button immediately after wrapper
                    if (section4ButtonContainer && !section4ButtonContainer.parentElement) {
                        targetParent.appendChild(section4ButtonContainer);
                    }
                }
                
                // Function to find and move the "ADD ALL TO CART" button
                function moveAddAllButton() {
                    // Find the button by its text content or class
                    var addAllButton = null;
                    var allButtons = document.querySelectorAll('button.oct-quiz-btn');
                    
                    for (var i = 0; i < allButtons.length; i++) {
                        var btn = allButtons[i];
                        var btnText = btn.textContent || btn.innerText || '';
                        if (btnText.trim().toUpperCase() === 'ADD ALL TO CART') {
                            addAllButton = btn;
                            break;
                        }
                    }
                    
                    // If button found and wrapper exists, move button after wrapper
                    if (addAllButton && sections123Wrapper.parentElement) {
                        // Remove button from its current location
                        if (addAllButton.parentElement) {
                            addAllButton.parentElement.removeChild(addAllButton);
                        }
                        
                        // Create a container for the button
                        var buttonContainer = document.createElement('div');
                        buttonContainer.className = 'octane-section-4-button';
                        buttonContainer.style.cssText = 'width: 100%; margin: 40px 0; text-align: center;';
                        buttonContainer.appendChild(addAllButton);
                        
                        // Insert button container right after the wrapper
                        if (sections123Wrapper.nextSibling) {
                            sections123Wrapper.parentElement.insertBefore(buttonContainer, sections123Wrapper.nextSibling);
                        } else {
                            sections123Wrapper.parentElement.appendChild(buttonContainer);
                        }
                        
                        return true; // Successfully moved
                    }
                    return false; // Button not found or wrapper not in DOM
                }
                
                // Try to move button immediately
                if (!moveAddAllButton()) {
                    // If not found immediately, try again after a short delay (button might be rendered by Octane AI)
                    setTimeout(function() {
                        moveAddAllButton();
                    }, 100);
                    
                    // Also try after a longer delay as fallback
                    setTimeout(function() {
                        moveAddAllButton();
                    }, 500);
                }
                
                // Reset async setting
                jQuery.ajaxSetup({ async: true });
            } else {
                // Old format: product_blocks is just an array of products
                // Flatten product blocks array
                var products = [].concat.apply([], sections);
                
                if (products.length === 0) {
                    if (quizContainer) quizContainer.classList.remove('overlayed');
                    return;
                }
                
                var productsContainer = document.createElement("div");
                productsContainer.classList.add('products-container');
                productsContainer.style.cssText = 'display: flex; flex-wrap: wrap; width: 100%; gap: 10px;';
                
                if (listToAppendTo && listToAppendTo.parentElement) {
                    listToAppendTo.parentElement.appendChild(productsContainer);
                } else {
                    list.appendChild(productsContainer);
                }
                
                // Set AJAX to synchronous to maintain product order
                jQuery.ajaxSetup({ async: false });
                
                // Process each product
                products.forEach(function(product, index) {
                    var template = productCardTemplate.cloneNode(true);
                    processProduct(product, index, template, vacationAnswer, productsContainer, section0Variant, scalpFeelAnswer);
                });
                
                // Reset async setting
                jQuery.ajaxSetup({ async: true });
            }
        } else {
            if (quizContainer) quizContainer.classList.remove('overlayed');
            return;
        }
        
        // Scroll to top of page after rendering is complete
        // Use setTimeout to ensure DOM updates are complete
        setTimeout(function() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            // Also try scrolling the document element as fallback
            if (document.documentElement) {
                document.documentElement.scrollTop = 0;
            }
            if (document.body) {
                document.body.scrollTop = 0;
            }
        }, 100);
        
        // Remove overlay when done
        if (quizContainer) {
            quizContainer.classList.remove('overlayed');
        }
    }

    // Handle variant selection change
    function handleVariantChange(event) {
        var selectVariant = event.target;
        var productCardContainer = selectVariant.closest('.recommended-product-card');
        if (!productCardContainer) return;
        
        var selectedOption = selectVariant.options[selectVariant.selectedIndex];
        var varImg = selectedOption.getAttribute('data-varimg');
        var variantId = selectVariant.value;
        
        // Update image
        var img = productCardContainer.querySelector('.image__container img');
        if (img && varImg) {
            img.src = varImg;
        }
        
        // Update add to cart link
        var addToCartBtn = productCardContainer.querySelector('.upsell-addtocart');
        if (addToCartBtn) {
            var linkVariant = CONFIG.cartAddUrl + "?id=" + variantId + "&quantity=1";
            addToCartBtn.setAttribute("href", linkVariant);
            addToCartBtn.setAttribute("data-variant", variantId);
        }

        // Swap the Octane persona copy to the newly picked scent
        updateVariantCopy(productCardContainer, productCardContainer.getAttribute('data-product-id'), variantId);

        // Update price
        var variantPrice = selectedOption.getAttribute('data-variant-price');
        var variantComparePrice = selectedOption.getAttribute('data-variant-compare-price');
        var priceEl = productCardContainer.querySelector('.price--end .money');
        var priceWithoutDiscount = productCardContainer.querySelector('.price--without-discount');
        var priceWithoutDiscountMoney = priceWithoutDiscount ? priceWithoutDiscount.querySelector('.money') : null;
        
        if (priceEl && variantPrice) {
            priceEl.innerText = "$" + parseFloat(variantPrice).toFixed(2);
            
            // Update aria-label based on whether there's a discount
            if (variantComparePrice) {
                priceEl.setAttribute("aria-label", "Discounted price");
                
                // Show and set original price
                if (priceWithoutDiscount && priceWithoutDiscountMoney) {
                    priceWithoutDiscountMoney.innerText = "$" + parseFloat(variantComparePrice).toFixed(2);
                    priceWithoutDiscount.style.display = "inline";
                }
            } else {
                priceEl.setAttribute("aria-label", "Price");
                
                // Hide original price
                if (priceWithoutDiscount) {
                    priceWithoutDiscount.style.display = "none";
                }
            }
        }
    }

    // Handle subscription selection change
    function handleSubscriptionChange(event) {
        var selectSubscr = event.target;
        var productCardContainer = selectSubscr.closest('.recommended-product-card');
        if (!productCardContainer) return;
        
        var addToCartBtn = productCardContainer.querySelector('.upsell-addtocart');
        if (!addToCartBtn) return;
        
        if (event.target.value > 2) {
            addToCartBtn.setAttribute("data-sp-id", event.target.value);
            var frequencyText = event.target.options[event.target.selectedIndex].innerText.replace("Save 20% - ", "Delivered every");
            addToCartBtn.setAttribute("data-frequency", frequencyText);
        } else {
            addToCartBtn.removeAttribute("data-sp-id");
            addToCartBtn.removeAttribute("data-frequency");
        }
    }

    // Initialize event listeners
    function init() {
        // Use event delegation for better performance
        document.addEventListener('change', function(event) {
            if (event.target.classList.contains('upsells_variants')) {
                handleVariantChange(event);
            } else if (event.target.classList.contains('subscriptions')) {
                handleSubscriptionChange(event);
            }
        });
        
        // Listen for Octane AI quiz events
        document.addEventListener('octane.quiz.accessed', function(e) {
            handleQuizResults(e);
        }, false);
        
        document.addEventListener('octane.quiz.completed', function(e) {
            handleQuizResults(e);
        }, false);
        
        // Also listen for any custom events that might be used
        document.addEventListener('octane.quiz.result', function(e) {
            handleQuizResults(e);
        }, false);
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            init();
        });
    } else {
        init();
    }

})();
